import React, { useState, useEffect } from 'react';
import { useToken } from 'app/hooks';
import API from 'utils/api';
import { message } from 'antd';
import styled from 'styled-components';

const MessageLogWrapper = styled.div`
	padding: 0;
	width: 100%;
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
`;

const Title = styled.h2`
	font-size: 24px;
	font-weight: 600;
	color: #111111;
`;

const Filters = styled.div`
	display: flex;
	gap: 12px;
	margin-bottom: 24px;
	flex-wrap: wrap;
`;

const FilterInput = styled.input`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 4px;
	font-size: 14px;
`;

const FilterSelect = styled.select`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 4px;
	font-size: 14px;
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
	border: 1px solid #f6f1f1;
	th {
		padding: 12px;
		background-color: #fff5f5;
		border: 1px solid #f6f1f1;
		text-align: left;
		font-weight: 600;
		color: #333333;
		font-size: 14px;
	}
	td {
		padding: 12px;
		border: 1px solid #f6f1f1;
		color: #111111;
		font-size: 14px;
	}
	tr:hover {
		background-color: #fafafa;
	}
`;

const StatusBadge = styled.span<{ success: boolean }>`
	padding: 4px 8px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 500;
	background-color: ${props => props.success ? '#e6f7e6' : '#ffe6e6'};
	color: ${props => props.success ? '#52c41a' : '#ff4d4f'};
`;

const ChannelBadge = styled.span<{ channel: 'sms' | 'alimtalk' | 'unknown' }>`
	padding: 4px 8px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 600;
	background-color: ${props =>
		props.channel === 'sms' ? '#e6f7ff' : props.channel === 'alimtalk' ? '#fffbe6' : '#f5f5f5'};
	color: ${props =>
		props.channel === 'sms' ? '#1677ff' : props.channel === 'alimtalk' ? '#d48806' : '#666'};
`;

const Pagination = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
	margin-top: 24px;
`;

const PageButton = styled.button<{ active?: boolean }>`
	padding: 6px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 4px;
	background-color: ${props => props.active ? '#F78B85' : '#fff'};
	color: ${props => props.active ? '#fff' : '#111'};
	cursor: pointer;
	&:hover {
		background-color: ${props => props.active ? '#F78B85' : '#fafafa'};
	}
`;

export const MessageLog = () => {
	const { data: token } = useToken();
	const [logs, setLogs] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);

	const formatDateYYYYMMDD = (d: Date) => {
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${yyyy}-${mm}-${dd}`;
	};

	const getDefaultDateRange = () => {
		const today = new Date();
		const endDate = formatDateYYYYMMDD(today);
		const start = new Date(today);
		start.setDate(start.getDate() - 7);
		const startDate = formatDateYYYYMMDD(start);
		return { startDate, endDate };
	};

	const [filters, setFilters] = useState({
		type: '',
		channel: '',
		...getDefaultDateRange(),
		phone: '',
		page: 1,
		maxData: 20
	});
	const [pagination, setPagination] = useState({
		totalCount: 0,
		maxPage: 1,
		currentPage: 1
	});

	const fetchLogs = async () => {
		setLoading(true);
		try {
			const params = new URLSearchParams();
			if (filters.type) params.append('type', filters.type);
			if (filters.channel) params.append('channel', filters.channel);
			if (filters.startDate) params.append('startDate', filters.startDate);
			if (filters.endDate) params.append('endDate', filters.endDate);
			if (filters.phone) params.append('phone', filters.phone);
			params.append('page', filters.page.toString());
			params.append('maxData', filters.maxData.toString());

			const res: any = await API.get(`/message/history?${params.toString()}`, token + '', {});
			if (res?.code === 0) {
				const result = res?.result || {};
				const list = result?.list || result?.result?.list || [];
				setLogs(Array.isArray(list) ? list : []);
				setPagination({
					totalCount: result?.totalCount || result?.result?.totalCount || 0,
					maxPage: result?.maxPage || result?.result?.maxPage || 1,
					currentPage: result?.currentPage || result?.result?.currentPage || filters.page
				});
			} else {
				message.error(res?.message || '조회에 실패했습니다.');
			}
		} catch (error: any) {
			message.error('조회 중 오류가 발생했습니다.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (token) {
			fetchLogs();
		}
	}, [filters, token]);

	const handleFilterChange = (key: string, value: any) => {
		setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
	};

	const handlePageChange = (page: number) => {
		setFilters(prev => ({ ...prev, page }));
	};

	return (
		<MessageLogWrapper>
			<Header>
				<Title>메시지 전송 로그</Title>
			</Header>

			<Filters>
				<FilterSelect
					value={filters.channel}
					onChange={(e) => handleFilterChange('channel', e.target.value)}
				>
					<option value="">전체 채널</option>
					<option value="sms">SMS</option>
					<option value="alimtalk">알림톡</option>
				</FilterSelect>
				<FilterSelect
					value={filters.type}
					onChange={(e) => handleFilterChange('type', e.target.value)}
				>
					<option value="">전체 타입</option>
					<option value="absence">결시</option>
					<option value="report">일지발송</option>
					<option value="attendance">등원</option>
				</FilterSelect>
				<FilterInput
					type="date"
					value={filters.startDate}
					onChange={(e) => handleFilterChange('startDate', e.target.value)}
					placeholder="시작일"
				/>
				<FilterInput
					type="date"
					value={filters.endDate}
					onChange={(e) => handleFilterChange('endDate', e.target.value)}
					placeholder="종료일"
				/>
				<FilterInput
					type="text"
					value={filters.phone}
					onChange={(e) => handleFilterChange('phone', e.target.value)}
					placeholder="전화번호 검색"
				/>
			</Filters>

			{loading ? (
				<div>로딩 중...</div>
			) : (
				<>
					<Table>
						<thead>
							<tr>
								<th>전송일시</th>
								<th>수신자</th>
								<th>전화번호</th>
								<th>타입</th>
								<th>채널</th>
								<th>템플릿코드</th>
								<th>상태</th>
								<th>에러메시지</th>
							</tr>
						</thead>
						<tbody>
							{logs.length === 0 ? (
								<tr>
									<td colSpan={7} style={{ textAlign: 'center', padding: '24px' }}>
										조회된 로그가 없습니다.
									</td>
								</tr>
							) : (
								logs.map((log, idx) => (
									<tr key={idx}>
										<td>{new Date(log.createdAt).toLocaleString('ko-KR')}</td>
										<td>{log.name}</td>
										<td>{log.phone}</td>
										<td>
											{log.type === 'absence' ? '결시' :
											 log.type === 'report' ? '일지발송' :
											 log.type === 'attendance' ? '등원' : log.type}
										</td>
										<td>
											{(() => {
												let channel: 'sms' | 'alimtalk' | 'unknown' = 'unknown'
												try {
													const parsed = log?.content ? JSON.parse(log.content) : null
													const c = parsed?.channel
													if (c === 'sms') channel = 'sms'
													else if (c === 'alimtalk' || c === 'kakao') channel = 'alimtalk'
												} catch (e) {
													// ignore JSON parse failures
												}

												// Fallback: legacy Alimtalk logs usually have templateCode
												if (channel === 'unknown' && log?.templateCode) channel = 'alimtalk'

												const label = channel === 'sms' ? 'SMS' : channel === 'alimtalk' ? '알림톡' : '기타'
												return <ChannelBadge channel={channel}>{label}</ChannelBadge>
											})()}
										</td>
										<td>{log.templateCode || '-'}</td>
										<td>
											<StatusBadge success={log.status}>
												{log.status ? '성공' : '실패'}
											</StatusBadge>
										</td>
										<td>{log.errorMessage || '-'}</td>
									</tr>
								))
							)}
						</tbody>
					</Table>

					{pagination.maxPage > 1 && (
						<Pagination>
							<PageButton
								onClick={() => handlePageChange(Math.max(1, pagination.currentPage - 1))}
								disabled={pagination.currentPage === 1}
							>
								이전
							</PageButton>
							{Array.from({ length: Math.min(5, pagination.maxPage) }, (_, i) => {
								const page = Math.max(1, Math.min(pagination.maxPage - 4, pagination.currentPage - 2)) + i;
								if (page > pagination.maxPage) return null;
								return (
									<PageButton
										key={page}
										active={page === pagination.currentPage}
										onClick={() => handlePageChange(page)}
									>
										{page}
									</PageButton>
								);
							})}
							<PageButton
								onClick={() => handlePageChange(Math.min(pagination.maxPage, pagination.currentPage + 1))}
								disabled={pagination.currentPage === pagination.maxPage}
							>
								다음
							</PageButton>
						</Pagination>
					)}
				</>
			)}
		</MessageLogWrapper>
	);
};

export default MessageLog;

